import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { Spinner, Offcanvas, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import "../css/Booking.css";
import { useTranslation } from "react-i18next";
import { formatRupiah } from "../utils/formatRupiah";
import {
  FaAngleDoubleLeft,
  FaUserTag,
  FaCoffee,
  FaConciergeBell,
} from "react-icons/fa";
import { formatDateTime } from "../helpers/dateFormat";

const Booking = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const dropdownRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [editingLeadGuest, setEditingLeadGuest] = useState(false);
  const [notes, setNotes] = useState("");
  const [fee] = useState(0);
  const [paymentChannel, setPaymentChannel] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("manual");
  const [transferProof, setTransferProof] = useState(null);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [offcanvasType, setOffcanvasType] = useState("");

  const [timeLeft, setTimeLeft] = useState(1200);
  const [terms, setTerms] = useState(null);
  const [error, setError] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState(null);
  const [agreed, setAgreed] = useState(false);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getSessionBooking = () => {
    try {
      const raw = sessionStorage.getItem("bookingData");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.warn("Failed parsing session bookingData", e);
      return null;
    }
  };

  const bookingDataFromLocation = location.state;
  const sessionBooking = getSessionBooking();
  const bookingData = bookingDataFromLocation || sessionBooking;

  useEffect(() => {
    if (bookingDataFromLocation) {
      try {
        sessionStorage.setItem(
          "bookingData",
          JSON.stringify(bookingDataFromLocation)
        );
      } catch (e) {
        console.warn("Failed to store bookingData in sessionStorage", e);
      }
    }
  }, [bookingDataFromLocation]);

  useEffect(() => {
    if (!bookingData) {
      const to = setTimeout(() => {
        navigate("/", { replace: true });
      }, 700);
      return () => clearTimeout(to);
    }
  }, [bookingData, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!user && !token) {
      navigate("/login", { state: { from: location } });
      return;
    }

    if (user) {
      setLeadName(user.name || "");
      setLeadPhone(user.phone_number || user.phone || "");
    } else {
      try {
        const raw = localStorage.getItem("user");
        if (raw && raw !== "undefined") {
          const storedUser = JSON.parse(raw);
          setLeadName(storedUser.name || "");
          setLeadPhone(storedUser.phone_number || storedUser.phone || "");
        }
      } catch (e) {}
    }
  }, [user, navigate, location]);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/terms-and-condition`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          const generalTerms = response.data.data.filter(
            (term) => term.type === "general"
          );
          setTerms(generalTerms.length > 0 ? generalTerms[0] : null);

          const payTerms = response.data.data.filter(
            (term) => term.type === "payment"
          );
          setPaymentTerms(payTerms.length > 0 ? payTerms[0] : null);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        setError("Failed to fetch terms and conditions.");
      }
    };

    fetchTerms();
  }, [i18n.language, t]);

  if (!bookingData) {
    return (
      <div className="booking-container">
        <p>{t("noBookingData")}</p>
      </div>
    );
  }

  const {
    roomType,
    computedPrice,
    adults,
    children,
    roomsCount,
    checkIn,
    checkOut,
    photos,
  } = bookingData;

  const roomNameDisplay =
    roomType && roomType.name ? roomType.name : "Room Type";

  const dayType = roomType?.day_type || "weekday";
  const totalPrice = computedPrice || roomType?.rate_plans?.[0]?.price || 0;
  const mainPhoto =
    photos && photos.length > 0
      ? photos[0].url
      : roomType && roomType.photos && roomType.photos.length > 0
      ? roomType.photos[0].url
      : "/assets/placeholder.jpg";

  const calcNights = () => {
    if (!checkIn || !checkOut) return 1;
    const inDate = new Date(checkIn);
    const outDate = new Date(checkOut);
    const diffTime = outDate - inDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  };
  const nights = calcNights();

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const options = { weekday: "short", month: "short", day: "numeric" };
    return new Date(dateStr).toLocaleDateString("en-US", options);
  };

  const submitReservation = async (opts = {}) => {
    setIsSaving(true);
    try {
      const inDate = new Date(checkIn).toISOString().split("T")[0];
      const outDate = new Date(checkOut).toISOString().split("T")[0];

      const payload = {
        room_type_id: roomType.id,
        check_in_date: inDate,
        check_out_date: outDate,
        number_of_rooms: roomsCount,
        guests: adults + children,
        rate_plan_code: roomType?.rate_plans?.[0]?.rate_plan_code || null,
        extras: [],
        notes: notes || "",
        idempotency_key: crypto.randomUUID(),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_APP_URL}/booking`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Platform": process.env.REACT_APP_X_PLATFORM,
            "API-Key": process.env.REACT_APP_API_KEY,
            "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === "success") {
        const resData = response.data.data;

        try {
          sessionStorage.removeItem("bookingData");
        } catch (e) {}

        Swal.fire({
          icon: "success",
          title: t("reservationSuccessful"),
          text: response.data.message || "Reservasi berhasil dibuat.",
          timer: 1400,
          showConfirmButton: false,
        });

        navigate("/confirmation", {
          state: { reservation: resData },
        });

        return resData;
      } else {
        Swal.fire({
          icon: "error",
          title: t("reservationFailed"),
          text: response.data.message || "Gagal membuat reservasi",
        });
        return null;
      }
    } catch (error) {
      console.error("Reservation error:", error);
      const errMsg =
        error.response?.data?.message ||
        error.message ||
        "Terjadi kesalahan saat membuat reservasi.";
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: errMsg,
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePesanSekarang = async () => {
    if (!agreed) {
      Swal.fire({
        icon: "error",
        title: "Perhatian",
        text:
          i18n.language === "ID"
            ? "Harap baca dan setujui Syarat & Ketentuan serta Kebijakan Pembayaran sebelum melanjutkan."
            : t("agreementTermCondition"),
        showConfirmButton: true,
      });
      return;
    }

    if (calcNights() <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Tanggal tidak valid",
        text: "Tanggal check-out harus lebih besar dari tanggal check-in.",
      });
      return;
    }

    const res = await Swal.fire({
      title: "Konfirmasi Pesanan",
      html: `
      <p>Apakah Anda yakin ingin menyelesaikan pemesanan ini?</p>
      <p><b>Segera lakukan pembayaran sebelum waktu pemesanan berakhir</b>.</p>
    `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya",
      cancelButtonText: "Batal",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    if (res.isConfirmed) {
      await submitReservation();
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      if (paymentMethod === "manual") {
        if (!transferProof) {
          Swal.fire({
            icon: "warning",
            title: t("transferProofRequiredTitle"),
            text: t("transferProofRequiredText"),
          });
          return;
        }
        if (!selectedBankId) {
          Swal.fire({
            icon: "warning",
            title: t("bankAccountRequiredTitle"),
            text: t("bankAccountRequiredText"),
          });
          return;
        }
      }
      await submitReservation();
      return;
    }
    if (currentStep === 3) {
      await submitReservation();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleEditLeadGuest = () => setEditingLeadGuest(true);
  const handleBackGuest = () => setEditingLeadGuest(false);

  const handleOpenOffcanvas = (type) => {
    setOffcanvasType(type);
    setShowOffcanvas(true);
  };
  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false);
    setOffcanvasType("");
  };

  return (
    <div className="booking-page">
      {isSaving && (
        <div className="fullscreen-loading">
          <div className="loader-container">
            <Spinner animation="border" variant="light" size="lg" />
            <h4 className="mt-3">
              {i18n.language === "ID"
                ? "Proses Reservasi"
                : t("processingReservation")}
            </h4>
          </div>
        </div>
      )}

      <div className="step-indicator">
        <div className={`step-item ${currentStep >= 1 ? "active" : ""}`}>
          <div className="step-circle">1</div>
          <span>{t("customerInformation")}</span>
        </div>
        <div className={`step-item ${currentStep >= 2 ? "active" : ""}`}>
          <div className="step-circle">2</div>
          <span>{t("paymentInformation")}</span>
        </div>
        <div className={`step-item ${currentStep >= 3 ? "active" : ""}`}>
          <div className="step-circle">{currentStep >= 3 ? "✓" : "3"}</div>
          <span>{t("bookingConfirmed")}</span>
        </div>
      </div>

      <div className="price-hold-banner">
        <p>
          {t("priceHoldMessage")}
          <span className="timer-count"> {formatTime(timeLeft)}</span>
        </p>
      </div>

      <div className="booking-content">
        <div className="booking-form-section">
          {currentStep === 1 && (
            <div className="step-form">
              <div className="lead-guest-card">
                <h5>{t("enterGuestDetail")}</h5>
                {!editingLeadGuest ? (
                  <div className="lead-guest-view">
                    <p className="lead-guest-name">{leadName}</p>
                    <p className="lead-guest-phone">{leadPhone}</p>
                    <button
                      className="edit-button"
                      onClick={handleEditLeadGuest}
                    >
                      {t("edit")}
                    </button>
                  </div>
                ) : (
                  <div className="lead-guest-edit">
                    <div className="input-group">
                      <label>{t("fullName")}</label>
                      <input
                        type="text"
                        value={leadName}
                        onChange={(e) => setLeadName(e.target.value)}
                      />
                    </div>
                    <div className="input-group">
                      <label>{t("phoneNumber")}</label>
                      <input
                        type="text"
                        value={leadPhone}
                        onChange={(e) => setLeadPhone(e.target.value)}
                        placeholder="e.g. 081234567890"
                      />
                    </div>
                    <button className="back-button" onClick={handleBackGuest}>
                      {t("back")}
                    </button>
                  </div>
                )}
              </div>
              <h5>{t("notesLabel")}</h5>
              <div className="input-group">
                <textarea
                  rows="4"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("notesPlaceholder")}
                ></textarea>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="step-form">
              <div className="payment">
                <div className="payment-right">
                  <div className="detail-payment-card">
                    <div className="detail-payment-header">
                      <h4>{t("bookingDetails")}</h4>
                      <button
                        className="book-more-btn"
                        onClick={() => navigate(-1)}
                      >
                        <FaAngleDoubleLeft />
                        {t("bookMore")}
                      </button>
                    </div>

                    <h3 className="room-name d-flex align-items-center justify-content-between">
                      {roomNameDisplay}
                      <Badge
                        bg={
                          dayType === "weekday"
                            ? "secondary"
                            : dayType === "weekend"
                            ? "success"
                            : dayType === "high_season"
                            ? "warning"
                            : dayType === "special_event"
                            ? "danger"
                            : "secondary"
                        }
                        className="text-capitalize"
                        style={{ fontSize: "0.8rem", padding: "6px 10px" }}
                      >
                        {dayType.replace("_", " ")}
                      </Badge>
                    </h3>

                    <div className="clickable-container">
                      <div
                        className="clickable-item"
                        onClick={() => handleOpenOffcanvas("breakfast")}
                      >
                        <FaCoffee className="clickable-icon" />
                        <span className="sub-info clickable-text">
                          {t("breakfast")}
                        </span>
                      </div>
                      <div
                        className="clickable-item"
                        onClick={() => handleOpenOffcanvas("facilities")}
                      >
                        <FaConciergeBell className="clickable-icon" />
                        <span className="sub-info clickable-text">
                          {t("facilities")}
                        </span>
                      </div>
                    </div>

                    <div className="check-dates">
                      <div>
                        <strong>{t("checkIn")}</strong> {formatDate(checkIn)}
                      </div>
                      <div>
                        <strong>{t("checkOut")}</strong> {formatDate(checkOut)}
                      </div>
                      <div>
                        <strong style={{ fontSize: 16 }}>
                          {adults + children}
                        </strong>{" "}
                        <FaUserTag size={18} />
                      </div>
                    </div>

                    <div>
                      {t("room")} x {roomsCount}
                    </div>

                    <div className="subtotal-row">
                      <span>{t("subTotal")}</span>
                      <span>{formatRupiah(totalPrice)}</span>
                    </div>

                    <div className="terms-arrangement">
                      <input
                        type="checkbox"
                        id="agreement"
                        required
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                      />
                      <label htmlFor="agreement" className="terms-line">
                        {t("byBookingYouAgree")}{" "}
                        <span
                          className="clickable-text"
                          onClick={() => handleOpenOffcanvas("terms")}
                        >
                          {t("terms")}
                        </span>{" "}
                        |{" "}
                        <span
                          className="clickable-text"
                          onClick={() => handleOpenOffcanvas("paymentTerms")}
                        >
                          {t("paymentTerms")}
                        </span>
                      </label>
                    </div>

                    <div className="total-row">
                      <span>{t("total")}</span>
                      <span>{formatRupiah(computedPrice)}</span>
                    </div>
                    <button
                      className="pay-now-btn"
                      onClick={handlePesanSekarang}
                    >
                      {i18n.language === "ID" ? "Pesan Sekarang" : "Book Now"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="step-form">
              <h2>{t("bookingConfirmed")}</h2>
              <p>
                {t("thankYou")} <strong>{leadName}</strong>.{" "}
                {t("reservationConfirmed")}
              </p>
              <p>
                {t("whatsappConfirmationSent")} <strong>{leadPhone}</strong>.
              </p>
              <p>{t("seeYouSoon")}</p>
            </div>
          )}

          <div className="form-navigation">
            {currentStep > 1 && (
              <button className="nav-button back" onClick={handleBack}>
                {t("back")}
              </button>
            )}
            {(currentStep === 1 || currentStep === 3) && (
              <button className="nav-button next" onClick={handleNext}>
                {currentStep === 1 ? t("next") : t("finish")}
              </button>
            )}
          </div>
        </div>

        <div className="booking-summary-card">
          <div className="dates-card">
            <h3>{t("yourStay")}</h3>
            <div className="dates-row">
              <div className="date-col">
                <span className="date-big">{formatDate(checkIn)}</span>
                <span className="time-small">15:00</span>
              </div>
              <span className="arrow">→</span>
              <div className="date-col">
                <span className="date-big">{formatDate(checkOut)}</span>
                <span className="time-small">12:00</span>
              </div>
              <div className="nights-col">
                <span className="nights-count">{nights}</span>
                <span className="nights-label">{t("perNight")}</span>
              </div>
            </div>
          </div>

          <img
            src={mainPhoto}
            alt={roomNameDisplay}
            className="summary-image"
          />
          <div className="summary-info">
            <h3 className="room-name">{roomNameDisplay}</h3>
            <div className="price-info">
              <p>{t("totalPrice")}</p>
              <h4>{formatRupiah(computedPrice)}</h4>
              <small>{t("taxes")}</small>
            </div>
          </div>
        </div>
      </div>

      <Offcanvas
        show={showOffcanvas}
        onHide={handleCloseOffcanvas}
        placement="end"
        style={{
          width: window.innerWidth < 768 ? "100%" : "80%",
          height: "100vh",
        }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {offcanvasType === "breakfast"
              ? i18n.language === "ID"
                ? "Detail Sarapan"
                : t("breakfastDetails")
              : offcanvasType === "terms"
              ? i18n.language === "ID"
                ? "Syarat & Ketentuan"
                : t("termsAndConditions")
              : offcanvasType === "paymentTerms"
              ? i18n.language === "ID"
                ? "Syarat dan Ketentuan Pembayaran"
                : t("paymentTermsTitle")
              : offcanvasType === "facilities"
              ? i18n.language === "ID"
                ? "Fasilitas"
                : t("facilitiesTitle")
              : i18n.language === "ID"
              ? "Beri Penilaian"
              : t("giveARating")}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {offcanvasType === "breakfast" && (
            <div className="offcanvas-content">
              <img
                src="/assets/breakfast.jpg"
                alt="Breakfast"
                className="offcanvas-image"
              />
              <p>{t("breakfastContent")}</p>
            </div>
          )}
          {offcanvasType === "terms" && (
            <div className="offcanvas-content">
              {terms ? (
                <>
                  <div dangerouslySetInnerHTML={{ __html: terms.content }} />
                  {paymentTerms && (
                    <p className="updated-at">
                      Last updated: {formatDateTime(paymentTerms.updated_at)}
                    </p>
                  )}
                </>
              ) : (
                <p>No terms and conditions found.</p>
              )}
            </div>
          )}
          {offcanvasType === "paymentTerms" && (
            <div className="offcanvas-content">
              {paymentTerms ? (
                <>
                  <div
                    dangerouslySetInnerHTML={{ __html: paymentTerms.content }}
                  />
                  <p className="updated-at">
                    Last updated: {formatDateTime(paymentTerms.updated_at)}
                  </p>
                </>
              ) : (
                <p>no data found</p>
              )}
            </div>
          )}
          {offcanvasType === "facilities" && (
            <div className="offcanvas-content">
              {roomType.facilities && roomType.facilities.length > 0 ? (
                roomType.facilities.map((facility, idx) => (
                  <div key={idx} className="facility-item">
                    <img
                      src={facility.image || "/assets/placeholder.jpg"}
                      alt={facility.name}
                      className="facility-image"
                    />
                    <div>
                      <h5>{facility.name}</h5>
                      <p>{facility.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p>{t("noFacilities")}</p>
              )}
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default Booking;
